import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Filter, Info } from 'lucide-react';
import { toast } from 'sonner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { AnalyticsFilters, formatDaysLabel } from './AnalyticsFilters';
import { ManualFollowUpDialog } from './ManualFollowUpDialog';
import { FunnelTrendCard } from './FunnelTrendCard';
import SmsConversionCard from './SmsConversionCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REAL_COLS = [
  ['call_attempts', 'Call Attempts'],
  ['spoke_to', 'Spoke To'],
  ['connect_rate', 'Connect %'],
  ['voicemail_no_answer', 'Voicemail / Not Answered'],
  ['inbound_total', 'Inbound'],
  ['inbound_engaged', 'Inbound Engaged'],
  ['wins', 'Wins'],
  ['losses', 'Losses'],
  ['revivable', 'Revivable'],
  ['next_step_scheduled', 'Next Step Scheduled (approx.)'],
  ['appointments_made', 'Appointments Made'],
  ['resigns', 'Resigns'],
  ['referral_mentioned', 'Referral Mentioned'],
];
const FU_COL = ['fu', 'Follow-ups C/P/O'];
const PENDING_COLS = [
  ['show_ratio', 'Show Ratio'],
  ['appointment_close_ratio', 'Appt Close Ratio'],
  ['referrals_from_pos', 'Referrals (POS)'],
];

// Plain-English column definitions — sourced from the aggregation logic in
// backend/routes/analytics.py::get_sales_funnel + core/call_outcome.py.
// Surfaced as an ⓘ tooltip on each column header so managers can answer
// "how is this number calculated?" without asking engineering.
const COL_DEFS = {
  call_attempts: 'Every outbound call the agent placed in the selected window — any length, any result. Internal staff-to-staff calls are excluded. Counted on the call date.',
  spoke_to: 'Outbound calls that lasted 45 seconds or longer — the locked platform threshold for a real conversation.',
  connect_rate: 'Spoke To ÷ Call Attempts — the share of outbound dials that became a real conversation.',
  voicemail_no_answer: 'Outbound calls under 45 seconds — voicemail drops, no-answers and instant hang-ups.',
  inbound_total: 'All inbound calls received in the window.',
  inbound_engaged: 'Inbound calls that were answered. New calls use the real phone-system answered signal; historical calls use the ≥45-second duration proxy.',
  wins: 'Revived deals explicitly marked "Won" after a follow-up chase (by the agent or a manager). Counted on the date the AI graded the deal.',
  losses: 'Deals marked "Lost" after a chase, plus AI-graded "Lost Deal" outcomes that were never later won.',
  revivable: 'Calls the AI graded as "Revivable Deal" — the caller deferred but is recoverable with a follow-up.',
  next_step_scheduled: 'The AI inferred a concrete next step was agreed on the call. An approximation — NOT a confirmed booking.',
  appointments_made: 'Appointments detected in the call transcript by AI extraction. The verbatim snippet is stored for audit.',
  resigns: 'The caller asked to cancel / resign on the call — extracted from the transcript.',
  referral_mentioned: 'A referral was mentioned anywhere in the call transcript.',
  fu: 'Won-deal follow-up tasks: Completed / Pending / Overdue. A star (★) counts completed follow-ups where a referral was detected on the linked call.',
  show_ratio: 'Pending the GymSales data feed — never estimated.',
  appointment_close_ratio: 'Pending the GymSales data feed — never estimated.',
  referrals_from_pos: 'Pending the GymSales data feed — never estimated.',
};

const HeaderTip = ({ colKey, label, muted = false }) => (
  <span className="inline-flex items-center gap-1">
    {label}
    <Tooltip>
      <TooltipTrigger asChild>
        <Info
          className={`w-3 h-3 shrink-0 cursor-help ${muted ? 'text-slate-200 hover:text-slate-400' : 'text-slate-300 hover:text-slate-500'}`}
          data-testid={`funnel-col-info-${colKey}`}
        />
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[280px] text-xs leading-snug normal-case tracking-normal">
        {COL_DEFS[colKey]}
      </TooltipContent>
    </Tooltip>
  </span>
);

const _rate = (m) => (m.call_attempts ? `${Math.round((100 * m.spoke_to) / m.call_attempts)}%` : '—');

const _withConnectRates = (d) => ({
  ...d,
  sites: (d?.sites || []).map(s => ({
    ...s,
    totals: { ...s.totals, connect_rate: _rate(s.totals) },
    agents: (s.agents || []).map(a => ({ ...a, connect_rate: _rate(a) })),
  })),
});

const SalesFunnelTab = ({ domainId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [site, setSite] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const backfillWins = async () => {
    if (!window.confirm('Create follow-up tasks for wins from the last 60 days? Older wins will show as overdue immediately.')) return;
    setBackfilling(true);
    try {
      const p = domainId ? `?domain_id=${domainId}` : '';
      const r = await axios.post(`${API}/follow-ups/backfill-wins${p}`);
      toast.success(`Created ${r.data.created} follow-up tasks (${r.data.wins_found} wins found)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally { setBackfilling(false); }
  };

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (domainId) p.set('domain_id', domainId);
    if (days) p.set('days', String(days));
    if (site) p.set('site', site);
    axios.get(`${API}/analytics/sales-funnel?${p}`, { timeout: 35000 })
      .then(r => setData(_withConnectRates(r.data)))
      .catch(() => setData({ sites: [] }))
      .finally(() => setLoading(false));
  }, [domainId, days, site, refreshKey]);

  const allAgents = (data?.sites || []).flatMap(s => s.agents || []);

  return (
    <div className="space-y-4" data-testid="sales-funnel-tab">
      <ManualFollowUpDialog
        open={showAddTask} onOpenChange={setShowAddTask} domainId={domainId}
        agents={allAgents} onCreated={() => setRefreshKey(k => k + 1)}
      />
      <AnalyticsFilters domainId={domainId} days={days} site={site} onDaysChange={setDays} onSiteChange={setSite} />
      <FunnelTrendCard domainId={domainId} site={site} />
      <SmsConversionCard domainId={domainId} days={days} compact />
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" /></div>
      ) : (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between gap-2">
              <span className="flex items-center gap-2"><Filter className="w-4 h-4" /> Sales Funnel by Site {days ? `— ${formatDaysLabel(days)}` : '— all time'}</span>
              <span className="flex items-center gap-2">
                <button
                  onClick={() => setShowAddTask(true)}
                  className="text-[11px] font-normal border border-teal-200 text-teal-600 rounded px-2 py-1 hover:border-teal-400"
                  data-testid="funnel-add-task-btn"
                >
                  + Add follow-up task
                </button>
                <button
                  onClick={backfillWins}
                  disabled={backfilling}
                  className="text-[11px] font-normal border border-slate-200 text-slate-500 rounded px-2 py-1 hover:border-slate-300 disabled:opacity-40"
                  data-testid="funnel-backfill-wins-btn"
                >
                  {backfilling ? 'Creating…' : 'Create tasks for past wins'}
                </button>
              </span>
            </CardTitle>
            <p className="text-xs text-slate-400">
              Buckets use the shared call-outcome field (outbound ≥45s = Spoke To). "Next Step Scheduled" is an AI-inferred approximation, not a booked appointment.
              Greyed columns are pending the GymSales data feed — never estimated.
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <TooltipProvider delayDuration={150}>
            <table className="w-full text-xs" data-testid="sales-funnel-table">
              <thead>
                <tr className="text-left text-slate-400 uppercase tracking-wider text-[10px] border-b">
                  <th className="py-2 pr-3">Site / Agent</th>
                  {REAL_COLS.map(([k, label]) => <th key={k} className="py-2 px-2 text-right"><HeaderTip colKey={k} label={label} /></th>)}
                  <th className="py-2 px-2 text-right"><HeaderTip colKey="fu" label={FU_COL[1]} /></th>
                  {PENDING_COLS.map(([k, label]) => (
                    <th key={k} className="py-2 px-2 text-right text-slate-300"><HeaderTip colKey={k} label={label} muted /></th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y">
                {(data?.sites || []).map(s => (
                  <React.Fragment key={s.site}>
                    <tr className="bg-slate-50 font-semibold" data-testid={`funnel-site-${s.site}`}>
                      <td className="py-2 pr-3">{s.site}</td>
                      {REAL_COLS.map(([k]) => <td key={k} className="py-2 px-2 text-right">{s.totals[k]}</td>)}
                      <td className="py-2 px-2 text-right">{s.totals.fu_completed}/{s.totals.fu_pending}/<span className={s.totals.fu_overdue ? 'text-red-500' : ''}>{s.totals.fu_overdue}</span>{s.totals.fu_referrals ? ` (${s.totals.fu_referrals}★)` : ''}</td>
                      {PENDING_COLS.map(([k]) => <td key={k} className="py-2 px-2 text-right text-slate-300">—</td>)}
                    </tr>
                    {s.agents.map(a => (
                      <tr key={a.agent_id} data-testid={`funnel-agent-${a.agent_id}`}>
                        <td className="py-1.5 pr-3 pl-6 text-slate-600">{a.agent_name}</td>
                        {REAL_COLS.map(([k]) => <td key={k} className="py-1.5 px-2 text-right text-slate-700">{a[k]}</td>)}
                        <td className="py-1.5 px-2 text-right text-slate-700" data-testid={`funnel-fu-${a.agent_id}`}>{a.fu_completed}/{a.fu_pending}/<span className={a.fu_overdue ? 'text-red-500' : ''}>{a.fu_overdue}</span>{a.fu_referrals ? ` (${a.fu_referrals}★)` : ''}</td>
                        {PENDING_COLS.map(([k]) => <td key={k} className="py-1.5 px-2 text-right text-slate-300">—</td>)}
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
                {(!data?.sites || data.sites.length === 0) && (
                  <tr><td colSpan={REAL_COLS.length + PENDING_COLS.length + 2} className="py-8 text-center text-slate-400">No calls in this window.</td></tr>
                )}
              </tbody>
            </table>
            </TooltipProvider>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SalesFunnelTab;
